import { EFileType } from '@/constant';
import { useMergingState } from '@/hooks';
import {
	convertAppleFileToJpgFile,
	convertPdfToImages,
	getImageType,
	insertObjectIfV2,
	onDownloadFileFromServer,
} from '@/utils';
import { useEffect, useMemo } from 'react';

import { isNilOrEmpty } from 'ramda-adjunct';

import { TDefineDocumentPreviewProps } from './define-document-preview.type';

export const useLogic = ({
	fileList,
	open,
	fetchPreview,
	setFetchPreview,
}: TDefineDocumentPreviewProps) => {
	const [state, setState] = useMergingState<{
		isLoading: boolean;
		imageUrls: string[] | null;
	}>({
		isLoading: false,
		imageUrls: null,
	});

	const imageUrls = useMemo(() => state.imageUrls, [state.imageUrls]);

	const generateFileToPreview = async () => {
		if (fileList.length === 0) return;

		setState({
			isLoading: true,
			imageUrls: null,
		});

		let imageUrls: string[] = [];

		for (const { sourceType, file } of fileList) {
			const { isHeicHeifFile, isPDFFile } = getImageType({
				...file,
				fileName: file?.name,
			});

			let blob: any = '';

			if (sourceType === EFileType.SERVER) {
				blob = await onDownloadFileFromServer(file?.path, '', {
					output: 'blob',
					createObjectURL: true,
					...insertObjectIfV2(isHeicHeifFile, {
						createObjectURL: false,
					}),
				});
			} else {
				blob = isHeicHeifFile ? file : URL.createObjectURL(file);
			}

			if (isNilOrEmpty(blob)) continue;

			if (isPDFFile) {
				const convertedImages = await convertPdfToImages(blob);

				if (convertedImages.length === 0) continue;

				convertedImages.forEach((pdfImage, i) => {
					imageUrls.push(pdfImage);
				});
				continue;
			}

			if (isHeicHeifFile) {
				const convertedImage = await convertAppleFileToJpgFile(blob as Blob, {
					quality: 0.92,
					output: 'blob',
					createObjectURL: true,
				});
				if (convertedImage) {
					imageUrls.push(convertedImage);
					continue;
				}
			} else {
				imageUrls.push(blob);
			}
		}

		setState({
			isLoading: false,
			imageUrls,
		});
	};

	useEffect(() => {
		if (!fileList?.length) return;

		const fetchFileToPreview = async () => {
			await generateFileToPreview();
		};

		if (!fetchPreview && open) {
			fetchFileToPreview();
			setFetchPreview?.(true);
		}
	}, [fetchPreview, open]);

	return {
		isLoading: state.isLoading,
		imageUrls,
		fileList,
	};
};
